import Color from '@/Enums/Color.enum';
import type {
  IFootTrafficWeekInsightRequest,
  IFootTrafficWeekInsightResponse,
} from '@/Interfaces/Attribution/FootTrafficWeekInsight.interface';
import { getUsHolidaysInRange } from '@/helpers/attribution/usHolidays';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { fetchFootTrafficWeekInsight } from '@/store/api/attribution.api';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  payload: IFootTrafficWeekInsightRequest | null;
}

const cache = new Map<string, IFootTrafficWeekInsightResponse>();

const cacheKey = (p: IFootTrafficWeekInsightRequest): string =>
  `${p.advertiserId}|${p.weekStartDate}|${p.weekTotals.impressions}|${p.weekTotals.visits}|${p.weekTotals.priorYearVisits ?? ''}|${(p.campaignNames ?? []).slice().sort().join(',')}|${p.locationIds.length}`;

const formatWeekRange = (weekStartISO: string): string => {
  const start = new Date(`${weekStartISO}T00:00:00`);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
};

const WeatherCard = ({
  year,
  avgHighF,
  avgLowF,
  totalPrecipIn,
  daysWithRain,
}: IFootTrafficWeekInsightResponse['weather'][number]) => (
  <Box
    sx={{
      flex: 1,
      p: 1.5,
      bgcolor: '#f9fafb',
      borderRadius: 1,
      border: `1px solid ${Color.Grey}33`,
    }}
  >
    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600 }}>{year}</Typography>
    {avgHighF == null ? (
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>No data</Typography>
    ) : (
      <>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mt: 0.5 }}>
          {Math.round(avgHighF)}° / {avgLowF != null ? `${Math.round(avgLowF)}°` : '—'}
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          {(totalPrecipIn ?? 0).toFixed(2)}″ over {daysWithRain} day{daysWithRain === 1 ? '' : 's'}
        </Typography>
      </>
    )}
  </Box>
);

const WeekInsightPanel = ({ open, onClose, payload }: Props) => {
  const [data, setData] = useState<IFootTrafficWeekInsightResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !payload) return;
    const key = cacheKey(payload);
    if (lastKey.current === key && data) return;
    lastKey.current = key;

    const cached = cache.get(key);
    if (cached) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }

    setData(null);
    setError(null);
    setLoading(true);
    fetchFootTrafficWeekInsight(payload)
      .then(res => {
        cache.set(key, res);
        setData(res);
      })
      .catch((e: Error) => setError(e.message || 'Failed to generate insight'))
      .finally(() => setLoading(false));
  }, [open, payload, data]);

  const holidays = payload
    ? getUsHolidaysInRange(
        payload.weekStartDate,
        new Date(new Date(`${payload.weekStartDate}T00:00:00`).getTime() + 6 * 86400000).toISOString().slice(0, 10),
      )
    : [];

  return (
    <Drawer anchor='right' open={open} onClose={onClose} PaperProps={{ sx: { width: 480 } }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Week Insight
            </Typography>
            {payload && (
              <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{formatWeekRange(payload.weekStartDate)}</Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        {holidays.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {holidays.map(h => (
              <Chip
                key={h.date}
                label={h.name}
                size='small'
                sx={{ mr: 1, bgcolor: `${Color.Primary}1a`, color: Color.Primary }}
              />
            ))}
          </Box>
        )}

        {payload && (
          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            <Box>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Visits</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                {numberToUsFormat(payload.weekTotals.visits)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Impressions</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                {numberToUsFormat(payload.weekTotals.impressions)}
              </Typography>
            </Box>
            {payload.weekTotals.priorYearVisits != null && payload.weekTotals.priorYearVisits > 0 && (
              <Box>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>YoY</Typography>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 600,
                    color: payload.weekTotals.visits >= payload.weekTotals.priorYearVisits ? '#0a7d3d' : '#b91c1c',
                  }}
                >
                  {(
                    ((payload.weekTotals.visits - payload.weekTotals.priorYearVisits) /
                      payload.weekTotals.priorYearVisits) *
                    100
                  ).toFixed(1)}
                  %
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600, mb: 1 }}>
          3-Year Weather Comparison
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {(data?.weather ?? []).map(w => (
            <WeatherCard key={w.year} {...w} />
          ))}
          {loading && !data && (
            <Box sx={{ flex: 1, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>

        <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600, mb: 1 }}>AI Insight</Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: '#f9fafb',
            borderRadius: 1,
            border: `1px solid ${Color.Grey}33`,
            minHeight: 80,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Generating insight…</Typography>
            </Box>
          ) : error ? (
            <Typography sx={{ fontSize: 13, color: '#b91c1c' }}>{error}</Typography>
          ) : data ? (
            <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>{data.narrative}</Typography>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
};

export default WeekInsightPanel;
